import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Mail, RefreshCw, Loader2, Sparkles, Send, ListTodo, ChevronRight,
  AlertTriangle, Paperclip, Building2, ExternalLink, Search, Power, CheckCircle2,
} from 'lucide-react';

const URGENCY_STYLES = {
  3: { bg: 'bg-rose-500/15', border: 'border-rose-500/40', text: 'text-rose-300', dot: 'bg-rose-400' },
  2: { bg: 'bg-amber-500/15', border: 'border-amber-500/40', text: 'text-amber-300', dot: 'bg-amber-400' },
  1: { bg: 'bg-blue-500/15', border: 'border-blue-500/40', text: 'text-blue-300', dot: 'bg-blue-400' },
  0: { bg: 'bg-zinc-700/40', border: 'border-zinc-700', text: 'text-zinc-400', dot: 'bg-zinc-500' },
};

const KIND_LABELS = {
  ai_reply_match: 'Réponse prospect',
  inbound_price_request: 'Demande de prix',
  other: 'Autre',
};
const KIND_COLORS = {
  ai_reply_match: 'border-emerald-700 text-emerald-300',
  inbound_price_request: 'border-amber-700 text-amber-300',
  other: 'border-zinc-700 text-zinc-400',
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function TodoPanel({ items, loading, onGenerate, onJump, generatedAt }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden" data-testid="mail-todo-panel">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
        <ListTodo className="w-4 h-4 text-amber-400" />
        <div className="text-sm font-semibold text-zinc-100 flex-1">TODO IA — emails à traiter</div>
        {generatedAt && (
          <span className="text-[10px] text-zinc-500">Généré {formatDate(generatedAt)}</span>
        )}
        <Button
          size="sm"
          onClick={onGenerate}
          disabled={loading}
          className="bg-amber-500 hover:bg-amber-600 text-zinc-950 h-7 text-xs font-semibold"
          data-testid="generate-todo-btn"
        >
          {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
          {items.length > 0 ? 'Régénérer' : 'Générer la TODO'}
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="p-6 text-center text-xs text-zinc-500">
          {loading ? 'Analyse IA en cours...' : 'Clique sur Générer pour obtenir la liste classée par urgence.'}
        </div>
      ) : (
        <div className="divide-y divide-zinc-800/60 max-h-[420px] overflow-y-auto">
          {items.map((it) => {
            const u = URGENCY_STYLES[it.urgency] || URGENCY_STYLES[1];
            return (
              <button
                key={it.id}
                onClick={() => onJump(it.id)}
                className="w-full text-left px-4 py-3 hover:bg-zinc-900/70 transition-colors flex items-start gap-3"
                data-testid={`todo-item-${it.id}`}
              >
                <div className={`mt-0.5 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded-sm ${u.bg} ${u.border} border ${u.text} shrink-0`}>
                  {it.urgency_label}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-100 font-medium">{it.action}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">{it.reason}</div>
                  <div className="text-[10px] text-zinc-600 mt-1 flex items-center gap-2 flex-wrap">
                    <span className="truncate max-w-[200px]">{it.from}</span>
                    <span>·</span>
                    <span className="truncate max-w-[260px]">{it.subject}</span>
                    {it.category && <Badge variant="outline" className="text-[9px] border-zinc-700 text-zinc-500">{it.category}</Badge>}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-600 mt-1 shrink-0" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InboxList({ items, activeId, onSelect, filter, setFilter, search, setSearch, refreshing, onRefresh }) {
  const filtered = items.filter((m) => {
    if (filter !== 'all' && m.kind !== filter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (m.from_email || '').toLowerCase().includes(q)
      || (m.from_name || '').toLowerCase().includes(q)
      || (m.subject || '').toLowerCase().includes(q)
      || (m.snippet || '').toLowerCase().includes(q);
  });
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm flex flex-col h-[calc(100vh-200px)] min-h-[480px]" data-testid="mail-inbox-list">
      <div className="px-3 py-2 border-b border-zinc-800 flex items-center gap-2 shrink-0">
        <Search className="w-3 h-3 text-zinc-500 shrink-0" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher..."
          className="bg-transparent border-0 h-7 focus-visible:ring-0 px-0 text-sm placeholder:text-zinc-600"
          data-testid="mail-search-input"
        />
        <Button size="sm" variant="ghost" onClick={onRefresh} disabled={refreshing} className="h-7 w-7 p-0 text-zinc-400 hover:text-amber-400 shrink-0" data-testid="mail-refresh-btn">
          {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        </Button>
      </div>
      <div className="px-2 py-2 border-b border-zinc-800 shrink-0">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="bg-zinc-950 h-8">
            <TabsTrigger value="all" className="text-[11px] h-6" data-testid="filter-all">Tous ({items.length})</TabsTrigger>
            <TabsTrigger value="inbound_price_request" className="text-[11px] h-6" data-testid="filter-pr">Devis</TabsTrigger>
            <TabsTrigger value="ai_reply_match" className="text-[11px] h-6" data-testid="filter-replies">Prospects</TabsTrigger>
            <TabsTrigger value="other" className="text-[11px] h-6" data-testid="filter-other">Autres</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="flex-1 overflow-y-auto" data-testid="mail-list">
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-xs text-zinc-500">Aucun email</div>
        ) : filtered.map((m) => (
          <button
            key={m.id}
            onClick={() => onSelect(m.id)}
            className={`w-full text-left px-3 py-2.5 border-b border-zinc-900 hover:bg-zinc-900/70 transition-colors ${activeId === m.id ? 'bg-zinc-900/80 border-l-2 border-l-amber-500' : ''}`}
            data-testid={`mail-item-${m.id}`}
          >
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <div className="text-sm text-zinc-200 font-medium truncate">{m.from_name || m.from_email}</div>
              <span className="text-[10px] text-zinc-500 shrink-0">{formatDate(m.created_at)}</span>
            </div>
            <div className="text-xs text-zinc-400 truncate">{m.subject || '(sans objet)'}</div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <Badge variant="outline" className={`text-[9px] ${KIND_COLORS[m.kind] || 'border-zinc-700 text-zinc-500'}`}>
                {KIND_LABELS[m.kind] || m.kind}
              </Badge>
              {m.has_attachments && (
                <span className="text-[10px] text-zinc-500 inline-flex items-center gap-0.5">
                  <Paperclip className="w-2.5 h-2.5" /> {m.attachments_count}
                </span>
              )}
              {m.replied && (
                <span className="text-[10px] text-emerald-400 inline-flex items-center gap-0.5">
                  <CheckCircle2 className="w-2.5 h-2.5" /> répondu
                </span>
              )}
              {m.company_name && (
                <span className="text-[10px] text-zinc-500 truncate inline-flex items-center gap-0.5">
                  <Building2 className="w-2.5 h-2.5" /> {m.company_name}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function EmailDetail({ logId, onSent }) {
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const load = useCallback(async () => {
    if (!logId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/gmail/inbox/${logId}`);
      setLog(data);
      setSubject(data.draft_subject || `Re: ${data.subject || ''}`);
      setBody(data.draft_body || '');
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [logId]);

  useEffect(() => { load(); }, [load]);

  const generateDraft = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/gmail/inbox/${logId}/draft-now`);
      setSubject(data.subject);
      setBody(data.body);
      toast.success('Brouillon IA généré');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur IA');
    } finally {
      setGenerating(false);
    }
  };

  const saveDraft = async () => {
    try {
      await api.put(`/gmail/inbox/${logId}/draft`, { draft_subject: subject, draft_body: body });
      toast.success('Brouillon enregistré');
    } catch {
      toast.error('Erreur sauvegarde');
    }
  };

  const sendReply = async () => {
    if (!body.trim()) {
      toast.error('Le corps du message est vide');
      return;
    }
    setSending(true);
    try {
      await api.post('/gmail/send-reply', {
        gmail_log_id: logId,
        subject,
        body,
      });
      toast.success('Réponse envoyée via Gmail ✉️');
      onSent && onSent();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  if (!logId) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm h-[calc(100vh-200px)] min-h-[480px] flex items-center justify-center text-zinc-500 text-sm">
        <div className="text-center">
          <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
          Sélectionne un email pour voir le détail
        </div>
      </div>
    );
  }

  if (loading || !log) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm h-[calc(100vh-200px)] min-h-[480px] flex items-center justify-center text-zinc-500 text-sm">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement...
      </div>
    );
  }

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm flex flex-col h-[calc(100vh-200px)] min-h-[480px]" data-testid="mail-detail">
      {/* Header */}
      <div className="px-5 py-4 border-b border-zinc-800 shrink-0">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0 flex-1">
            <div className="text-base font-semibold text-zinc-100" data-testid="mail-detail-subject">{log.subject || '(sans objet)'}</div>
            <div className="text-xs text-zinc-400 mt-1">
              <strong className="text-zinc-200">{log.from_name || ''}</strong> &lt;{log.from_email}&gt; · <span className="text-zinc-500">{formatDate(log.created_at)}</span>
            </div>
          </div>
          <Badge variant="outline" className={`text-[10px] ${KIND_COLORS[log.kind] || 'border-zinc-700 text-zinc-400'}`}>
            {KIND_LABELS[log.kind] || log.kind}
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          {log.company_name && (
            <Badge variant="outline" className="border-blue-700 text-blue-300">
              <Building2 className="w-3 h-3 mr-1" /> {log.company_name}
            </Badge>
          )}
          {log.intent && (
            <Badge variant="outline" className={
              log.intent === 'positive_reply' ? 'border-emerald-700 text-emerald-300'
                : log.intent === 'negative_reply' ? 'border-rose-700 text-rose-300'
                : 'border-zinc-700 text-zinc-400'
            }>
              {log.intent}
            </Badge>
          )}
          {log.has_attachments && (
            <Badge variant="outline" className="border-zinc-700 text-zinc-400">
              <Paperclip className="w-3 h-3 mr-1" /> {log.attachments_count} PJ
            </Badge>
          )}
          {log.replied && (
            <Badge variant="outline" className="border-emerald-700 text-emerald-300">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Répondu {formatDate(log.replied_at)}
            </Badge>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Body */}
        <div className="px-5 py-4 border-b border-zinc-800">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Email reçu</div>
          <div className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed" data-testid="mail-detail-body">
            {log.body_text || log.snippet || '(corps vide)'}
          </div>
          {log.attachments && log.attachments.length > 0 && (
            <div className="mt-3 pt-3 border-t border-zinc-800/60">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Pièces jointes</div>
              <div className="space-y-1">
                {log.attachments.map((a, i) => (
                  <div key={i} className="text-xs text-zinc-400 inline-flex items-center gap-2 mr-3">
                    <Paperclip className="w-3 h-3" /> {a.filename} <span className="text-zinc-600">({Math.round((a.size || 0) / 1024)} Ko)</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* AI suggested reply */}
        <div className="px-5 py-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <div className="text-sm font-semibold text-zinc-100">Réponse suggérée par l'IA</div>
            <Button size="sm" variant="outline" onClick={generateDraft} disabled={generating}
              className="ml-auto h-7 text-xs border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="mail-generate-draft-btn">
              {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
              {log.draft_body ? 'Régénérer' : 'Générer'}
            </Button>
          </div>
          <div className="space-y-2">
            <div>
              <div className="text-[10px] uppercase text-zinc-500 mb-1">Sujet</div>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="bg-zinc-950 border-zinc-800 h-9 text-sm"
                data-testid="mail-reply-subject"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase text-zinc-500 mb-1">Corps du message</div>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                placeholder="Le brouillon IA apparaîtra ici. Tu peux l'éditer librement avant d'envoyer."
                className="bg-zinc-950 border-zinc-800 text-sm font-mono leading-relaxed"
                data-testid="mail-reply-body"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer actions */}
      <div className="px-5 py-3 border-t border-zinc-800 flex items-center justify-between gap-2 shrink-0 bg-zinc-950/50">
        <Button variant="outline" size="sm" onClick={saveDraft}
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-9" data-testid="mail-save-draft-btn">
          Enregistrer le brouillon
        </Button>
        <Button
          onClick={sendReply}
          disabled={sending || !body.trim()}
          className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 font-semibold"
          data-testid="mail-send-reply-btn"
        >
          {sending
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi…</>
            : <><Send className="w-4 h-4 mr-2" /> Envoyer la réponse</>}
        </Button>
      </div>
    </div>
  );
}

export default function MailPage() {
  const [status, setStatus] = useState(null);
  const [inbox, setInbox] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [todo, setTodo] = useState({ items: [], generated_at: null });
  const [todoLoading, setTodoLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/gmail/status');
      setStatus(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  const fetchInbox = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get('/gmail/inbox?limit=150');
      setInbox(data);
    } catch (e) {
      toast.error("Erreur de chargement de l'inbox");
    } finally {
      setRefreshing(false);
    }
  }, []);

  const fetchCachedTodo = useCallback(async () => {
    try {
      const { data } = await api.get('/gmail/todo');
      setTodo(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchInbox();
    fetchCachedTodo();
  }, [fetchStatus, fetchInbox, fetchCachedTodo]);

  const scanNow = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post('/gmail/scan-now');
      if (data?.ok) {
        toast.success(`${data.processed} email(s) traité(s)`);
      } else {
        toast.error(data?.reason || 'Scan échoué');
      }
      await fetchInbox();
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur scan');
    } finally {
      setRefreshing(false);
    }
  };

  const generateTodo = async () => {
    setTodoLoading(true);
    try {
      const { data } = await api.post('/gmail/todo/generate?limit=30');
      setTodo(data);
      if (data.items.length === 0) {
        toast.info("L'IA n'a identifié aucune action à traiter");
      } else {
        toast.success(`${data.items.length} action(s) générée(s)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur IA');
    } finally {
      setTodoLoading(false);
    }
  };

  // Not connected state
  if (status && !status.connected) {
    return (
      <div className="space-y-4" data-testid="mail-page-disconnected">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center shadow-lg shadow-rose-500/20">
            <Mail className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-zinc-100" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Mails</h1>
            <p className="text-xs text-zinc-500">Boîte de réception ProdPilot pilotée par IA</p>
          </div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-8 text-center" data-testid="mail-disconnected-card">
          <Power className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
          <div className="text-lg font-semibold text-zinc-200 mb-2">Gmail n'est pas encore connecté</div>
          <p className="text-sm text-zinc-500 mb-6 max-w-md mx-auto">
            Pour utiliser cette page, connecte ta boîte Gmail dans les paramètres. L'agent IA pourra alors lire tes emails, te générer une TODO classée par urgence et te proposer des réponses prêtes à envoyer.
          </p>
          <Button onClick={() => window.location.href = '/settings'} className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="mail-go-settings-btn">
            <ExternalLink className="w-4 h-4 mr-2" /> Aller aux Paramètres
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="mail-page">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-sm bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center shadow-lg shadow-rose-500/20">
          <Mail className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-zinc-100" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Mails</h1>
          <p className="text-xs text-zinc-500">
            {status?.profile_email && <>Connecté à <strong className="text-zinc-300">{status.profile_email}</strong> · </>}
            {status?.last_scan_at ? `Dernier scan : ${formatDate(status.last_scan_at)}` : 'Aucun scan effectué'}
          </p>
        </div>
        <Button onClick={scanNow} disabled={refreshing} variant="outline"
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-9" data-testid="mail-scan-now-btn">
          {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Scanner Gmail
        </Button>
      </div>

      {status?.last_scan_skipped_reason && (
        <div className="bg-amber-500/5 border border-amber-500/30 rounded-sm p-3 text-xs text-amber-300 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Dernier scan ignoré : {status.last_scan_skipped_reason}
        </div>
      )}

      {/* TODO panel */}
      <TodoPanel
        items={todo.items || []}
        loading={todoLoading}
        onGenerate={generateTodo}
        onJump={(id) => setActiveId(id)}
        generatedAt={todo.generated_at}
      />

      {/* 2 columns: inbox + detail */}
      <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-4">
        <InboxList
          items={inbox}
          activeId={activeId}
          onSelect={setActiveId}
          filter={filter} setFilter={setFilter}
          search={search} setSearch={setSearch}
          refreshing={refreshing} onRefresh={fetchInbox}
        />
        <EmailDetail logId={activeId} onSent={() => { fetchInbox(); }} />
      </div>
    </div>
  );
}
